import moment from 'moment';

import { Clipboard } from '@angular/cdk/clipboard';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';

import * as DateTimeRegex from '../../../constants';
import { SIMPLE_DATE_REGEX } from '../../../constants';
import { UiHelpersService } from '../../services';
import { DatetimeInputDialogComponent } from './datetime-input-dialog/datetime-input-dialog.component';

@Component({
  selector: 'phoenix-datetime-input',
  templateUrl: './datetime-input.component.html',
  styleUrl: './datetime-input.component.scss',
  standalone: false,
})
export class DatetimeInputComponent implements OnInit, OnChanges {
  @Input() type: 'date' | 'datetime-local' = 'datetime-local';
  @Input() placeholder = 'Date';
  @Input() init = false;
  @Input() fillOnFocus = true;
  @Input() minDate!: string;
  @Input() maxDate!: string;
  @Output() date = new EventEmitter<string>();
  @ViewChild('tooltip') tooltip!: MatTooltip;

  _control: FormControl = new FormControl('');
  @Input() set control(data: FormControl) {
    if (data) {
      this._control = data;
    }
  }

  /**
   * Identifies this instance's native input. Required because QA writes test
   * cases against it: a generated id would shift with the number of inputs the
   * session has mounted, so the host names it (see `DateRangeComponent`).
   * Deliberately not derived from `type` either — that follows the value, and
   * the id has to point at the same element across a format switch.
   */
  @Input({ required: true }) inputId!: string;
  copyTooltipText = 'Copy';
  editValue = false;
  pastedText: string = '';
  isValid = false;
  /**
   * The format the host asked for. `updateInput` switches `type` locally to
   * render a pasted value, so the original has to be kept to restore it.
   */
  private initialType: 'date' | 'datetime-local' = 'datetime-local';

  readonly dialog = inject(MatDialog);
  readonly clipboard = inject(Clipboard);
  readonly _uiHelper = inject(UiHelpersService);

  ngOnInit(): void {
    this.initialType = this.type;
    this.init && this._control.patchValue(this.generateDate());
  }

  setDefaultDateTime() {
    if (
      this.fillOnFocus &&
      !this._control.value &&
      this.type === 'datetime-local'
    ) {
      const time = this.maxDate ? '00:00' : '23:59';
      const date = moment.utc(this.minDate ?? undefined).toISOString();
      const dateString = date.split('T')[0];
      this._control.patchValue(`${dateString}T${time}`);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only a rebind from the host moves the baseline — the local switches in
    // `updateInput` never reach `ngOnChanges`.
    if (changes?.['type']) {
      this.initialType = changes['type'].currentValue;
    }

    if (changes?.['minDate'] && this.minDate) {
      this.minDate = this.cleanUpDate(changes?.['minDate'].currentValue);
      this._control.updateValueAndValidity();
    }

    if (changes?.['maxDate'] && this.maxDate) {
      this.maxDate = this.cleanUpDate(changes?.['maxDate'].currentValue);
      this._control.updateValueAndValidity();
    }
  }

  cleanDate() {
    this._control.patchValue('');
    this._control.markAsDirty();
    // A date-only paste leaves the native input on `type="date"`, which has no
    // time section. Clearing has to restore the host's format, otherwise a
    // time can never be entered again.
    this.type = this.initialType;
  }

  onPasteValidISODate() {
    this.pastedText = this.pastedText.replace(/[zZ]/, ''); // remove Zulu
    this.pastedText = this.pastedText.replace(/%3A/g, ':'); // URL decode
    const date = moment(this.pastedText, true);
    this.isValid =
      date.isValid() &&
      (DateTimeRegex.SIMPLE_DATE_REGEX.test(this.pastedText) ||
        DateTimeRegex.ISO8601_DATE_REGEX.test(this.pastedText));
  }

  async paste() {
    try {
      const pastedText = await navigator.clipboard.readText();
      this.pastedText = pastedText.replace(/[zZ]/, '').replace(/%3A/g, ':');
      this.onPasteValidISODate();

      if (this.isValid) {
        this.updateInput(this.pastedText);
      } else {
        this.openDialog();
      }
    } catch (error) {
      console.error('Error to read from the clipboard:', error);
    }
  }

  updateInput(value: string) {
    this.type = SIMPLE_DATE_REGEX.test(value) ? 'date' : 'datetime-local';
    setTimeout(() => {
      this._control.setValue(value);
      this._control.markAsDirty();
      this.date.emit(value);
      this._control.updateValueAndValidity();
    });
  }

  openDialog() {
    const dialogRef = this.dialog.open(DatetimeInputDialogComponent, {
      width: '500px',
      data: { initialValue: this.pastedText },
    });

    dialogRef.afterClosed().subscribe(val => {
      if (val) {
        this.updateInput(val.replace(/[zZ]/, '').replace(/%3A/g, ':'));
      }
    });
  }

  // Copy the current value of the date input
  copy() {
    const input = document.getElementById(this.inputId) as HTMLInputElement;
    if (input) {
      let val = input.value;
      if (val == '') return;
      val = SIMPLE_DATE_REGEX.test(val) ? val : `${val}Z`;
      this.clipboard.copy(val);

      this.copyTooltipText = 'Copied!';
      setTimeout(() => {
        this.tooltip.show();
        setTimeout(() => {
          this.copyTooltipText = 'Copy';
        }, 1000);
      }, 100);
    }
  }

  onDateValueChange(): void {
    const value = SIMPLE_DATE_REGEX.test(this._control.value)
      ? this._control.value
      : `${this._control.value}Z`;
    this._control.updateValueAndValidity();
    this.date.emit(value);
  }

  /**
   * Generate the date to init the input
   * @returns { string } - Today
   * @private
   */
  private generateDate(): string {
    const date = new Date();
    let today: string;
    if (this.type === 'date') {
      const year = date.getFullYear();
      let month = date.getMonth().toString();
      let day = date.getDay().toString();
      month.length === 1 && (month = `0${month}`);
      day.length === 1 && (day = `0${day}`);
      today = `${year}-${month}-${day}`;
    } else {
      return new Date().toISOString().replace(/[zZ]/, '');
    }
    return today;
  }

  private cleanUpDate(date: string) {
    if (SIMPLE_DATE_REGEX.test(date)) {
      return `${date}T00:00`;
    }
    return date;
  }
}
